import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CustomerLayout } from "@/components/Layout/CustomerLayout";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { MODULE_ICONS } from "@/lib/serviceModules";
import { ChevronLeft, Star, MapPin, Sparkles, Loader2 } from "lucide-react";

export default function ProviderList() {
  const { key } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const [module, setModule] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/service-modules").then(r => r.data.find(x => x.key === key)),
      api.get("/providers", { params: { module: key } }).then(r => r.data),
    ]).then(([m, ps]) => {
      setModule(m);
      setProviders(ps);
      setLoading(false);
    });
  }, [key]);

  if (loading || !module) {
    return (
      <CustomerLayout>
        <div className="p-16 flex items-center justify-center text-[var(--so-text-soft)]">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      </CustomerLayout>
    );
  }

  const Icon = MODULE_ICONS[key] || Sparkles;
  const label = lang === "ar" ? module.label_ar : module.label_en;
  const desc = lang === "ar" ? module.description_ar : module.description_en;
  const accent = module.accent;

  return (
    <CustomerLayout>
      <div className="relative">
        {/* Hero header */}
        <div className="relative h-40 md:h-52 overflow-hidden" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}CC)` }}>
          <button onClick={() => navigate("/app")} className="absolute top-4 start-4 w-10 h-10 rounded-full bg-white/25 backdrop-blur flex items-center justify-center text-white" data-testid="module-back-btn">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="absolute inset-0 flex items-center max-w-5xl mx-auto px-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center text-white">
                <Icon className="w-7 h-7" />
              </div>
              <div className="text-white">
                <h1 className="text-2xl md:text-3xl font-extrabold" data-testid="module-title">{label}</h1>
                <p className="text-white/90 text-sm mt-0.5">{desc}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="text-lg font-bold">{lang === "ar" ? "المزودون القريبون" : "Providers near you"}</h2>
            <span className="text-sm text-[var(--so-text-soft)]" data-testid="providers-count">{providers.length}</span>
          </div>

          {providers.length === 0 ? (
            <div className="so-card p-12 text-center text-[var(--so-text-soft)]" data-testid="providers-empty">
              {lang === "ar" ? "لا يوجد مزودون بعد. تحقق قريبًا." : "No providers yet. Check back soon."}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 so-stagger">
              {providers.map(p => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/app/service/${key}/provider/${p.id}`)}
                  data-testid={`provider-card-${p.id}`}
                  className="so-card overflow-hidden text-start"
                >
                  <div className="h-36 bg-gray-100 dark:bg-gray-800">
                    {p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" loading="lazy" />}
                  </div>
                  <div className="p-4">
                    <div className="font-bold text-lg truncate">{p.name}</div>
                    <div className="text-xs text-[var(--so-text-soft)] mt-0.5 line-clamp-2">{p.description}</div>
                    <div className="mt-3 flex items-center gap-3 text-xs">
                      <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" /> <b>{p.rating}</b></span>
                      <span className="flex items-center gap-1 text-[var(--so-text-soft)]"><MapPin className="w-3.5 h-3.5" /> {p.city}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </CustomerLayout>
  );
}
